import React, { useCallback, useEffect, useState } from 'react';
import { useRowPick, PickedChip } from '../components/DataTable';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { inr, dt, day, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useLocationFilter } from '../context/LocationContext';
import { CloseRegisterModal } from '../components/RegisterModals';
import HeaderSlot from '../components/HeaderSlot';
import CashCount, { countsTotal, countsSummary } from '../components/CashCount';

// End of day for one branch: every register session settled, the takings totalled
// by payment mode, and the cash counted. Once closed, billing is refused for that
// date at that branch until a manager reopens it.
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CloseDayModal = ({ day: data, locName, onClose, onClosed }) => {
  const [counts, setCounts] = useState({});
  const [touched, setTouched] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const countedCash = countsTotal(counts);
  const variance = Math.round((countedCash - Number(data.expectedCash || 0)) * 100) / 100;

  const close = async () => {
    setBusy(true); setError('');
    try {
      const { data: r } = await api.post('/pos/day/close', {
        locationId: data.locationId, date: data.date, countedCash, countedCounts: counts, note,
      });
      toast.success(`${data.date} closed`);
      onClosed(r.dayClose); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Close ${day(data.date)} — ${locName || 'branch'}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={close} disabled={busy || !touched || data.openSessions > 0}>{busy ? 'Closing…' : `Close the day · ${inr(countedCash)}`}</button></>}>
      {data.openSessions > 0 && (
        <p className="form-error" style={{ marginTop: 0 }}>
          {data.openSessions} register{data.openSessions === 1 ? ' is' : 's are'} still open. Close {data.openSessions === 1 ? 'it' : 'them'} first — each drawer has to be counted by whoever held it.
        </p>
      )}
      <div className="table-wrap">
        <table className="grid">
          <tbody>
            <tr><td>Bills</td><td className="right">{data.salesCount}</td></tr>
            <tr><td>Takings</td><td className="right">{inr(data.salesTotal)}</td></tr>
            <tr><td>Cash sales</td><td className="right">{inr(data.byMode?.cash)}</td></tr>
            <tr><td>UPI</td><td className="right muted">{inr(data.byMode?.upi)}</td></tr>
            <tr><td>Card</td><td className="right muted">{inr(data.byMode?.card)}</td></tr>
            {Number(data.byMode?.platform) > 0 && <tr><td>Platform</td><td className="right muted">{inr(data.byMode.platform)}</td></tr>}
            {Number(data.byMode?.credit) > 0 && <tr><td>Credit / on account</td><td className="right muted">{inr(data.byMode.credit)}</td></tr>}
            <tr><td>Open balances</td><td className="right">{inr(data.openingCash)}</td></tr>
            {Number(data.refundsCash) > 0 && <tr><td>Cash refunds</td><td className="right">−{inr(data.refundsCash)}</td></tr>}
            {Number(data.cashExpenses) > 0 && (
              <tr><td>Cash paid out (expenses)</td><td className="right" title={(data.expenses || []).map((e) => `${e.category} ${e.amount}`).join(' · ')}>−{inr(data.cashExpenses)}</td></tr>
            )}
          </tbody>
          <tfoot><tr className="total-row"><td><strong>Cash expected in hand</strong></td><td className="right"><strong>{inr(data.expectedCash)}</strong></td></tr></tfoot>
        </table>
      </div>
      <div style={{ marginTop: '.7rem' }}>
        <CashCount counts={counts} onChange={(c) => { setCounts(c); setTouched(true); }} compact label="Cash in hand — count it note by note" />
      </div>
      <div className="field" style={{ marginTop: '.5rem' }}><label>Note</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Handover, deposit slip no., reason for a difference…" />
      </div>
      {touched && (
        <p className={Math.abs(variance) < 0.5 ? 'muted small' : 'form-error'}>
          {Math.abs(variance) < 0.5 ? 'Counted cash matches the day’s takings.' : variance > 0 ? `Over by ${inr(variance)}.` : `Short by ${inr(-variance)}.`}
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

const DayClose = () => {
  const { user, can } = useAuth();
  const { locations, locationId: locFilter } = useLocationFilter();
  const locationId = String(locFilter || user?.locationId || '');
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [closingDay, setClosingDay] = useState(false);
  const [closeSession, setCloseSession] = useState(null);
  const pick = useRowPick();   // hold a register while reading across its takings

  const load = useCallback(() => {
    if (!locationId) { setData(null); return; }
    setLoading(true);
    api.get(`/pos/day?locationId=${locationId}&date=${date}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [locationId, date]);
  useEffect(load, [load]);

  const canClose = can('sales:close');
  const locName = (locations.find((l) => String(l.id) === String(locationId)) || {}).name;
  const closed = data?.dayClose;

  const reopen = async () => {
    if (!window.confirm(`Reopen ${date} for ${locName}? Cashiers will be able to bill on this date again.`)) return;
    try { await api.post('/pos/day/reopen', { locationId: Number(locationId), date }); toast.success('Day reopened'); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <HeaderSlot>
        <input className="topbar-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} max={todayStr()} title="Trading day" />
      </HeaderSlot>

      <div className="section-head">
        <div />
        {data && (closed
          ? (canClose && <button className="btn btn-outline" onClick={reopen}>Reopen day</button>)
          : !data.traded
            ? <span className="chip-static">Nothing to close</span>
            : (canClose && (
              <button className="btn btn-primary" onClick={() => setClosingDay(true)} disabled={data.openSessions > 0}
                title={data.openSessions > 0 ? 'A register is still open — close it first' : undefined}>
                Close the day
              </button>
            )))}
      </div>

      {!locationId && <div className="card card-pad empty">Choose a branch in the top bar to see its register day.</div>}
      {loading && <div className="spinner" />}

      {!loading && data && (
        <>
          {closed && (
            <p className="muted small" style={{ marginTop: '-.2rem' }}>
              Closed {dt(closed.closedAt)} · cash in hand <span title={countsSummary(closed.countedCounts) || undefined}>{inr(closed.countedCash)}</span> against {inr(closed.expectedCash)} expected
              {Math.abs(Number(closed.cashVariance)) >= 0.5 && <span className="neg"> · {Number(closed.cashVariance) > 0 ? 'over' : 'short'} {inr(Math.abs(Number(closed.cashVariance)))}</span>}
              {closed.note ? ` · ${closed.note}` : ''}
            </p>
          )}
          {!closed && data.lastClose?.status === 'reopened' && (
            <p className="muted small" style={{ marginTop: '-.2rem' }}>Reopened {dt(data.lastClose.reopenedAt)} after an earlier close.</p>
          )}

          {!closed && !data.traded && (
            <p className="muted" style={{ marginTop: '-.2rem' }}>
              {data.beforeGoLive
                ? <>This branch was not billing here {data.goLive ? <>until {day(data.goLive)}</> : 'yet'} — nothing to close.</>
                : <>No register was opened and nothing was billed on this day — nothing to close.</>}
            </p>
          )}
          {!closed && data.openSessions > 0 && (
            <p className="form-error" style={{ marginTop: '-.2rem' }}>
              Still open: {data.sessions.filter((s) => s.status === 'open').map((s) => `${s.code} (${s.user?.name || '—'})`).join(', ')}.
              {' '}The day can be closed once every drawer has been counted.
            </p>
          )}
          {data.sessions.some((s) => Math.abs(Number(s.openingVariance || 0)) > 1) && (
            <p className="form-error" style={{ marginTop: '-.2rem' }}>
              A register opened out of tally with the previous close — see the opening column below; hover the figure for the reason given.
            </p>
          )}
          {data.traded && (
            <div className="stat-strip">
              <div className="strip-cell"><span className="k">Bills</span><span className="v">{data.salesCount}</span></div>
              <div className="strip-cell"><span className="k">Takings</span><span className="v">{inr(data.salesTotal)}</span></div>
              <div className="strip-cell"><span className="k">Cash</span><span className="v">{inr(data.byMode?.cash)}</span></div>
              <div className="strip-cell"><span className="k">UPI</span><span className="v">{inr(data.byMode?.upi)}</span></div>
              <div className="strip-cell"><span className="k">Card</span><span className="v">{inr(data.byMode?.card)}</span></div>
              {Number(data.byMode?.credit) > 0.005 && <div className="strip-cell"><span className="k">On credit</span><span className="v">{inr(data.byMode.credit)}</span></div>}
              {Number(data.cashExpenses) > 0 && (
                <div className="strip-cell neg-v"><span className="k">Paid out</span><span className="v">−{inr(data.cashExpenses)}</span></div>
              )}
              <div className="strip-cell lead"><span className="k">Cash expected</span><span className="v">{inr(data.expectedCash)}</span></div>
              <div className="strip-cell">
                <span className="k">Registers</span>
                <span className="v">{data.sessions.length}</span>
                {data.openSessions > 0 && <span className="sub neg">{data.openSessions} open</span>}
              </div>
            </div>
          )}

          {data.traded && <div className="card card-pad">
            <div className="section-head" style={{ marginTop: 0 }}><h3 style={{ margin: 0 }}>Registers</h3><PickedChip pick={pick} /></div>
            <div className="table-wrap">
              <table className="grid">
                <thead><tr><th className="freeze-col">Register</th><th>Cashier</th><th>Opened</th><th>Closed</th><th className="right">Open balance</th><th className="right">Bills</th><th className="right">Takings</th><th className="right">Cash · UPI · Card</th><th className="right">Cash expected</th><th className="right">Cash in hand</th><th>Status</th>{canClose && <th></th>}</tr></thead>
                <tbody>
                  {data.sessions.map((s) => {
                    const v = s.status === 'closed' ? Number(s.cashVariance) : null;
                    return (
                      <tr key={s.id} {...pick.markProps(s.id)}>
                        <td {...pick.cellProps(s.id)}><strong>{s.code}</strong></td>
                        <td>{s.user?.name || `#${s.userId}`}</td>
                        <td className="muted small">{dt(s.openedAt)}</td>
                        <td className="muted small">{s.closedAt ? dt(s.closedAt) : '—'}{s.forcedClose ? ' · forced' : ''}</td>
                        <td className="right">
                          {inr(s.openingCash)}
                          {Math.abs(Number(s.openingVariance || 0)) > 1 && (
                            <div className="neg" style={{ fontSize: '.72rem' }}
                              title={s.openingNote || undefined}>
                              {Number(s.openingVariance) > 0 ? 'over' : 'short'} {inr(Math.abs(Number(s.openingVariance)))} vs last close
                            </div>
                          )}
                        </td>
                        <td className="right">{s.salesCount}</td>
                        <td className="right">{inr(s.salesTotal)}</td>
                        <td className="right muted small">
                          {inr(s.byMode?.cash)} · {inr(s.byMode?.upi)} · {inr(s.byMode?.card)}
                        </td>
                        <td className="right">{inr(s.expectedCash)}</td>
                        <td className="right" title={countsSummary(s.closingCounts) || undefined}>
                          {s.status === 'closed' ? (
                            <>
                              {inr(s.countedCash)}
                              {v != null && Math.abs(v) >= 0.5 && (
                                <div className="neg" style={{ fontSize: '.72rem' }}>
                                  {v > 0 ? 'over' : 'short'} by {inr(Math.abs(v))}
                                </div>
                              )}
                            </>
                          ) : '—'}
                        </td>
                        <td><span className={`badge ${s.status === 'open' ? 'badge-gold' : 'badge-green'}`}>{s.status}</span></td>
                        {canClose && <td className="right">{s.status === 'open' && <button className="btn btn-outline btn-sm" onClick={() => setCloseSession(s)}>Close</button>}</td>}
                      </tr>
                    );
                  })}
                  {!data.sessions.length && <tr><td colSpan={canClose ? 12 : 11} className="empty">No register was opened on this date.</td></tr>}
                </tbody>
              </table>
            </div>
            {data.salesCount > 0 && !data.sessions.length && (
              <p className="muted small">This day has bills but no register session — they were rung up before day close was introduced.</p>
            )}
          </div>}
        </>
      )}

      {closingDay && data && <CloseDayModal day={data} locName={locName} onClose={() => setClosingDay(false)} onClosed={load} />}
      {closeSession && <CloseRegisterModal session={closeSession} onClose={() => setCloseSession(null)} onClosed={load} />}
    </div>
  );
};

export default DayClose;
